/**
 * Persistence for the Area Border checkbox (HP-8599).
 *
 * Border visibility used to live only in the URL (`?border=false`) and the map
 * state cache, so any load without that param — an email link, a new tab, a
 * plain reload — fell back to border-on and the user's deselection was lost.
 * The toggle now writes the choice here and the map-state hydrators read it
 * back when the URL carries no explicit `border` value.
 *
 * Read/write are standalone functions rather than `useLocalStorage` because the
 * hydrators need the stored value synchronously inside the same effect that
 * seeds the cache; a post-mount state sync would seed border-on first and flash
 * the boundary before correcting itself.
 */

export const BORDER_PREFERENCE_STORAGE_KEY = 'hp-area-border';

/** Stored Area Border choice, or null when the user has never toggled it. */
export function readStoredBorderPreference(): boolean | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem(BORDER_PREFERENCE_STORAGE_KEY);
    if (stored === null) return null;
    const parsed: unknown = JSON.parse(stored);
    return typeof parsed === 'boolean' ? parsed : null;
  } catch {
    // localStorage unavailable or invalid JSON — treat as no preference
    return null;
  }
}

export function writeStoredBorderPreference(showBorder: boolean): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(BORDER_PREFERENCE_STORAGE_KEY, JSON.stringify(showBorder));
  } catch {
    // Silently fail if localStorage is not available
  }
}

/**
 * Border visibility for a page load. An explicit `border` value in the URL wins
 * so shared/deep links keep their semantics; otherwise the stored preference
 * applies, and users who never toggled the checkbox keep the default.
 */
export function resolveInitialShowBorder(initialShowBorder: boolean, hasExplicitBorderParam: boolean): boolean {
  if (hasExplicitBorderParam) return initialShowBorder;
  return readStoredBorderPreference() ?? initialShowBorder;
}
